import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { ApiEndpoints } from '../../../core/api-endpoints';
import { AcademicYearService } from '../../../services/academic-year';
import { FlashService } from '../../../services/flash';
import { DecodedToken, Session } from '../../../services/session';
import { UserService } from '../../../services/user';
import { IUser } from '../../../interfaces/user';
import { ShimmerEffect } from '../../../shared/shimmer-effect/shimmer-effect';
import { Router } from '@angular/router';
import { DashboardService, IEnrollmentTrade } from '../../../services/dashboard';
import { Helpers } from '../../../utilities/helpers';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { AcademicTermService } from '../../../services/academic-term';
import { IAcademicTerms } from '../../../interfaces/academic-term';

Chart.register(...registerables);
Chart.defaults.font.family = "'Manrope', 'sans-serif'";
Chart.defaults.font.size = 13;
Chart.defaults.color = '#374151';

@Component({
  selector: 'app-dashboard-overview',
  imports: [ShimmerEffect],
  templateUrl: './dashboard-overview.html',
  styleUrl: './dashboard-overview.css',
})
export class DashboardOverview implements AfterViewInit {
  constructor(
    protected academicYearService: AcademicYearService,
    private session: Session,
    private userService: UserService,
    private router: Router,
    private dashboardService: DashboardService,
    protected academicTermService: AcademicTermService,
  ) {
    const token = this.session.getItem('token');

    this.loggedUser.set(this.session.decodeToken(token));

    if (!this.loggedUser()?.sub) {
      return;
    }

    if (this.loggedUser()?.role !== 'SUPER_ADMIN') {
      this.getAcademicYears();
      this.getDashboardData();
      this.getLatestActivities();
      this.getClassSummary();
      this.getAcademicTerms();
    }

    this.getUser(this.loggedUser()?.sub);

    effect(() => {
      if (!this.chartCanvas() || !this.hasEnrollmentData()) {
        return;
      }

      this.renderChart();
    });
  }

  ngAfterViewInit(): void {
    if (this.loggedUser()?.role !== 'SUPER_ADMIN') {
      this.getEnrollmentTrend();
    }
  }

  protected currentAcademicYear = signal<string>('');
  protected readonly flashService = inject(FlashService);
  protected loggedUser = signal<DecodedToken | null>(null);
  protected user = signal<IUser | null>(null);
  protected isLoading = signal<boolean>(false);
  protected dashboardData = signal<any>(null);
  protected latestActivities = signal<any>(null);
  protected enrollmentTrend = signal<IEnrollmentTrade | null>(null);
  protected hasEnrollmentData = computed(() => (this.enrollmentTrend()?.payload?.length ?? 0) > 0);
  protected classSummary = signal<any>(null);
  protected activePercent = signal<number>(0);
  protected inactivePercent = signal<number>(0);
  protected archivedPercent = signal<number>(0);
  protected totalClasses = signal<number>(0);
  protected chart!: Chart;
  protected readonly Helpers = Helpers;
  protected academicTerms = signal<IAcademicTerms | null>(null);
  protected chartCanvas = viewChild<ElementRef<HTMLCanvasElement>>('enrollmentChart');
  protected chartSupported = signal(true);

  protected getAcademicYears(): void {
    const params: Record<string, boolean | number> = {
      page: 0,
      size: 5,
      current_academic_year: true,
    };

    this.academicYearService
      .getAcademicYears(`${ApiEndpoints.url}/academic-year`, params)
      .subscribe({
        next: (res) => {
          this.currentAcademicYear.set(res.payload[0].name);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Dashboard. Refresh the page.',
          );
        },
      });
  }

  protected getUser(id: string | undefined): void {
    this.isLoading.set(true);

    this.userService.getUserById(`${ApiEndpoints.url}/user/${id}`).subscribe({
      next: (data) => {
        this.user.set(data);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Failed to load user details');
      },
    });
  }

  protected getDashboardData(): void {
    this.isLoading.set(true);

    this.dashboardService
      .getDashboardData(`${ApiEndpoints.url}/dashboard/overview-stats`)
      .subscribe({
        next: (res) => {
          this.dashboardData.set(res.payload);
          this.isLoading.set(false);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Failed to load dashboard. Refresh the page.',
          );
        },
      });
  }

  protected getLatestActivities(): void {
    this.isLoading.set(true);

    this.dashboardService
      .getDashboardData(`${ApiEndpoints.url}/dashboard/latest-activities`)
      .subscribe({
        next: (res) => {
          this.latestActivities.set(res.payload);
          this.isLoading.set(false);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Failed to load dashboard. Refresh the page.',
          );
        },
      });
  }

  protected getEnrollmentTrend(): void {
    this.isLoading.set(true);

    this.dashboardService
      .getEnrollmentTrend(`${ApiEndpoints.url}/dashboard/enrollment-trend`)
      .subscribe({
        next: (res) => {
          this.enrollmentTrend.set(res);
          this.isLoading.set(false);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Failed to load dashboard. Refresh the page.',
          );
        },
      });
  }

  protected getClassSummary(): void {
    this.isLoading.set(true);

    this.dashboardService
      .getDashboardData(`${ApiEndpoints.url}/dashboard/class-summary`)
      .subscribe({
        next: (res) => {
          this.classSummary.set(res.payload);
          this.totalClasses.set(res.payload.total_classes);

          if (this.totalClasses() === 0) {
            this.totalClasses.set(1);
          }

          this.activePercent.set((res.payload.active_classes / this.totalClasses()) * 100);

          this.inactivePercent.set((res.payload.inactive_classes / this.totalClasses()) * 100);

          this.archivedPercent.set((res.payload.archived_classes / this.totalClasses()) * 100);

          this.isLoading.set(false);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Failed to load dashboard. Refresh the page.',
          );
        },
      });
  }

  protected logout(): void {
    this.session.clear();
    void this.router.navigate(['/']);
  }

  private renderChart(): void {
    try {
      const labels = (this.enrollmentTrend()?.payload ?? []).map((item) => item.month);
      const dataPoints = (this.enrollmentTrend()?.payload ?? []).map((item) => item.no_of_students);

      const canvasEl = this.chartCanvas()?.nativeElement;

      if (!canvasEl) {
        return;
      }

      const ctx = canvasEl.getContext('2d')!;

      // Resolve the CSS variable to its actual computed value
      const primaryColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--primary-color')
        .trim();

      // Vertical gradient fill under the line, derived from --primary-color
      const gradientFill = ctx.createLinearGradient(0, 0, 0, 300);
      gradientFill.addColorStop(0, `color-mix(in srgb, ${primaryColor} 35%, transparent)`);
      gradientFill.addColorStop(1, `color-mix(in srgb, ${primaryColor} 0%, transparent)`);

      const config: ChartConfiguration<'line'> = {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'Student Enrollments',
              data: dataPoints,
              borderColor: primaryColor,
              backgroundColor: gradientFill,
              borderWidth: 3,
              tension: 0.4,
              fill: true,
              pointRadius: 0,
              pointHoverRadius: 6,
              pointHoverBackgroundColor: primaryColor,
              pointHoverBorderColor: '#fff',
              pointHoverBorderWidth: 2,
              pointBackgroundColor: primaryColor,
              cubicInterpolationMode: 'monotone',
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              display: false,
            },
            title: {
              display: true,
              text: 'Enrollment Trend',
              align: 'start',
              font: {
                size: 18,
                weight: 'bold',
              },
              padding: { bottom: 4 },
              color: '#111827',
            },
            subtitle: {
              display: true,
              text: 'Last 6 months',
              align: 'start',
              font: {
                size: 13,
                weight: 'normal',
              },
              padding: { bottom: 24 },
              color: '#6B7280',
            },
            tooltip: {
              backgroundColor: '#111827',
              titleColor: '#F9FAFB',
              bodyColor: '#F9FAFB',
              padding: 12,
              cornerRadius: 8,
              displayColors: false,
              titleFont: { size: 13, weight: 'bold' },
              bodyFont: { size: 13 },
              callbacks: {
                label: (context) =>
                  `${context.parsed.y} enrollment${context.parsed.y === 1 ? '' : 's'}`,
              },
            },
          },
          scales: {
            x: {
              grid: {
                display: false,
              },
              border: {
                display: false,
              },
              ticks: {
                color: '#6B7280',
                font: { size: 12 },
              },
            },
            y: {
              beginAtZero: true,
              grid: {
                color: '#F3F4F6',
              },
              border: {
                display: false,
              },
              ticks: {
                stepSize: 1,
                color: '#6B7280',
                font: { size: 12 },
                padding: 8,
              },
            },
          },
        },
      };

      if (this.chart) {
        this.chart.destroy();
      }

      this.chart = new Chart(canvasEl, config);
    } catch {
      this.chart?.destroy();
      this.chartSupported.set(false);
    }
  }

  protected getAcademicTerms(): void {
    const params: Record<string, boolean | number> = {
      page: 0,
      size: 1,
      current_term: true,
    };

    this.academicTermService
      .getAcademicTerms(`${ApiEndpoints.url}/academic-term`, params)
      .subscribe({
        next: (res) => {
          this.academicTerms.set(res);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Academic Terms. Refresh the page.',
          );
        },
      });
  }
}
